import axios from "axios";
import { useCallback } from "react";
import { getAuthToken } from "../context/AuthContext";

// Fix: Make sure to include trailing slash if necessary
const baseUrl =
  (process.env.REACT_APP_BACKEND_URL || "http://localhost:9000/") +
  "api/company";

const api = axios.create({
  baseURL: baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add error logging and response transformation
api.interceptors.response.use(
  (response) => {
    console.log("API Response:", response);
    return response;
  },
  (error) => {
    console.error("API Error:", error.response || error);
    return Promise.reject(error);
  },
);

const useCompany = () => {
  // Use useCallback to memoize these functions so they don't change on re-renders
  const getCurrentCompany = useCallback(async () => {
    try {
      const response = await api.get(`/current`);
      return response.data;
    } catch (error) {
      console.error("Error fetching current company:", error);
      throw error;
    }
  }, []);

  const updateCompany = useCallback(async (companyId, companyData) => {
    console.log("Updating company with ID:", companyId, "Data:", companyData);
    try {
      const response = await api.put(`/${companyId}`, companyData);
      return response.data;
    } catch (error) {
      console.error("Error updating company:", error);
      throw error;
    }
  }, []);

  const getAllCompanies = useCallback(async () => {
    try {
      const response = await api.get("/");
      return response.data;
    } catch (error) {
      console.error("Error fetching all companies:", error);
      throw error;
    }
  }, []);

  const addCompany = useCallback(async (companyData) => {
    try {
      const response = await api.post("/", companyData);
      return response.data;
    } catch (error) {
      console.error("Error adding company:", error);
      throw error;
    }
  }, []);

  const deleteCompany = useCallback(async (companyId) => {
    try {
      await api.delete(`/${companyId}`);
      return true;
    } catch (error) {
      console.error("Error deleting company:", error);
      throw error;
    }
  }, []);

  const uploadLogo = useCallback(async (imageData, companyId) => {
    try {
      const url = companyId ? `/logo/upload/${companyId}` : "/logo/upload";
      const response = await api.post(url, { imageData });
      return response.data;
    } catch (error) {
      console.error("Error uploading logo:", error);
      throw error;
    }
  }, []);

  const deleteLogo = useCallback(async (companyId) => {
    try {
      const url = companyId ? `/logo/${companyId}` : "/logo";
      const response = await api.delete(url);
      return response.data;
    } catch (error) {
      console.error("Error deleting logo:", error);
      throw error;
    }
  }, []);

  const getLogoUrl = useCallback(async (companyId) => {
    try {
      const response = await api.get(`/logo/${companyId}`);
      return response.data;
    } catch (error) {
      // Don't log 404s as errors (company simply has no logo)
      if (error.response?.status !== 404) {
        console.error("Error fetching logo URL:", error);
      }
      return null;
    }
  }, []);

  return {
    getCurrentCompany,
    updateCompany,
    getAllCompanies,
    addCompany,
    deleteCompany,
    uploadLogo,
    deleteLogo,
    getLogoUrl,
  };
};

export default useCompany;
